/**
 * La piel de Smarty — qué tema eligió esta persona y cómo se aplica.
 *
 * Todo vive en atributos de <html> (data-tema, data-marca, data-ancho) que lee temas.css. «Clásico», «De la
 * piel» y «Normal» son quitar los atributos: Smarty como siempre. Se elige en «Apariencia» (el menú de la
 * cuenta y el de Chats) y se guarda en este navegador.
 *
 * ⭐ LOS OSCUROS SE APOYAN EN EL MODO OSCURO QUE YA EXISTE, NO LO DUPLICAN. polaris-dark.css e index.css
 *    pintan con html.dark; Noche y Smarty oscuro agregan la clase y cambian la paleta encima. La clave del
 *    modo (sf_app_theme, la de useAppTheme) se escribe también, y al volver a Clásico se devuelve el modo que
 *    la persona tenía antes.
 */

import { injectFontLink } from '@/components/themes/fontLoader';

export type Tema = 'clasico' | 'nitido' | 'contraste' | 'noche' | 'smarty' | 'smarty-oscuro';
export type Marca = 'original' | 'clasico' | 'azul' | 'naranja';
export type Ancho = 'normal' | 'amplio';

export interface PreferenciaDeTema {
  tema: Tema;
  marca: Marca;
  ancho: Ancho;
}

export const TEMAS: ReadonlyArray<{ id: Tema; nombre: string }> = [
  { id: 'clasico', nombre: 'Clásico' },
  { id: 'nitido', nombre: 'Nítido' },
  { id: 'contraste', nombre: 'Contraste' },
  { id: 'noche', nombre: 'Noche' },
  // Las dos que salen de la página de inicio de smarty.lat (claro y oscuro, como allá).
  { id: 'smarty', nombre: 'Smarty' },
  { id: 'smarty-oscuro', nombre: 'Smarty oscuro' },
];

/** Los temas oscuros: llevan html.dark (el modo oscuro de siempre) y su paleta encima. */
const OSCUROS: ReadonlySet<Tema> = new Set<Tema>(['noche', 'smarty-oscuro']);
export const MARCAS: ReadonlyArray<{ id: Marca; nombre: string }> = [
  // La de Polaris en cualquier piel: el negro en claro y el azul del modo oscuro (founder, 21-oct). Va primero, como
  // Clásico en la fila de temas.
  { id: 'clasico', nombre: 'Clásico' },
  { id: 'original', nombre: 'De la piel' },
  { id: 'azul', nombre: 'Azul' },
  { id: 'naranja', nombre: 'Naranja' },
];
export const ANCHOS: ReadonlyArray<{ id: Ancho; nombre: string }> = [
  { id: 'normal', nombre: 'Normal' },
  { id: 'amplio', nombre: 'Amplio' },
];

/** Sin atributos en <html>: Polaris como siempre. */
const NEUTRO: PreferenciaDeTema = { tema: 'clasico', marca: 'original', ancho: 'normal' };

/**
 * Con la que arranca quien nunca eligió (founder, 21-oct): Nítido con la marca Clásico, también para quien
 * venía usando el modo oscuro —lo vuelve a elegir en «Apariencia»—.
 */
export const PREFERENCIA_POR_DEFECTO: PreferenciaDeTema = { tema: 'nitido', marca: 'clasico', ancho: 'normal' };

const CLAVE = 'smarty.tema';
const CLAVE_PROBAR = 'smarty.tema.probar';
/** La misma clave que usa useAppTheme para el modo claro/oscuro. */
const CLAVE_MODO_APP = 'sf_app_theme';
const CLAVE_MODO_PREVIO = 'smarty.tema.modoPrevio';

/*
 * 🪤 localStorage PUEDE FALLAR (ventana privada, sitio bloqueado). Un tema que no se puede guardar
 *    no puede impedir que el panel arranque: cada lectura y escritura pasa por acá.
 */
function leer(clave: string): string | null {
  try {
    return window.localStorage.getItem(clave);
  } catch {
    return null;
  }
}
function escribir(clave: string, valor: string | null): void {
  try {
    if (valor === null) window.localStorage.removeItem(clave);
    else window.localStorage.setItem(clave, valor);
  } catch {
    /* sin almacenamiento: el tema dura hasta recargar */
  }
}

/** La bandera de prueba: prende las herramientas del equipo (el botón flotante y el revisor), no el tema. */
export function conHerramientasDePrueba(): boolean {
  return import.meta.env.VITE_TEMAS_PRUEBA === '1' || leer(CLAVE_PROBAR) === '1';
}

export function leerPreferencia(): PreferenciaDeTema {
  const crudo = leer(CLAVE);
  if (!crudo) return PREFERENCIA_POR_DEFECTO;
  try {
    const p = JSON.parse(crudo) as Partial<PreferenciaDeTema>;
    return {
      tema: TEMAS.some((t) => t.id === p.tema) ? (p.tema as Tema) : PREFERENCIA_POR_DEFECTO.tema,
      marca: MARCAS.some((m) => m.id === p.marca) ? (p.marca as Marca) : PREFERENCIA_POR_DEFECTO.marca,
      ancho: ANCHOS.some((a) => a.id === p.ancho) ? (p.ancho as Ancho) : PREFERENCIA_POR_DEFECTO.ancho,
    };
  } catch {
    return PREFERENCIA_POR_DEFECTO;
  }
}

function ponerAtributo(nombre: string, valor: string, neutro: string): void {
  const html = document.documentElement;
  if (valor === neutro) html.removeAttribute(nombre);
  else html.setAttribute(nombre, valor);
}

/*
 * 🪤 UN TEMA CLARO CON html.dark ES UNA MEZCLA ROTA: si el modo oscuro venía prendido (desde Chats, o al
 *    salir de Noche), Nítido quedaba con variables claras y oscuras a la vez. Por eso cada tema nuevo fija
 *    su modo: Noche es oscuro, Nítido y Contraste son claros. Clásico devuelve el que la persona tenía.
 */
function sincronizarModoOscuro(tema: Tema): void {
  const html = document.documentElement;
  if (tema === 'clasico') {
    const previo = leer(CLAVE_MODO_PREVIO);
    // Nunca eligió un tema nuevo: su modo claro/oscuro no es asunto de este módulo.
    if (previo === null) return;
    escribir(CLAVE_MODO_PREVIO, null);
    escribir(CLAVE_MODO_APP, previo);
    const oscuro = previo === 'dark' || (previo === 'system' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
    html.classList.toggle('dark', Boolean(oscuro));
    return;
  }
  if (leer(CLAVE_MODO_PREVIO) === null) escribir(CLAVE_MODO_PREVIO, leer(CLAVE_MODO_APP) ?? 'light');
  const oscuro = OSCUROS.has(tema);
  escribir(CLAVE_MODO_APP, oscuro ? 'dark' : 'light');
  html.classList.toggle('dark', oscuro);
}

export function aplicarPreferencia(p: PreferenciaDeTema): void {
  sincronizarModoOscuro(p.tema);
  // Las pieles Smarty llevan los títulos en Poppins, la letra de la página de inicio.
  if (p.tema === 'smarty' || p.tema === 'smarty-oscuro') injectFontLink('poppins');
  ponerAtributo('data-tema', p.tema, NEUTRO.tema);
  ponerAtributo('data-marca', p.marca, NEUTRO.marca);
  ponerAtributo('data-ancho', p.ancho, NEUTRO.ancho);
}

export function guardarPreferencia(p: PreferenciaDeTema): void {
  escribir(CLAVE, JSON.stringify(p));
  aplicarPreferencia(p);
}
